import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOidcUser } from '@axa-fr/react-oidc';
import { getOidcConfigName } from 'utils/functions';
import { useMutation, useSubscription, useQuery } from '@apollo/client'; // eslint-disable-line no-restricted-imports
import { GET_MY_NOTIFICATIONS } from './queries';
import { MARK_NOTIFICATION_AS_READ, DISMISS_NOTIFICATION, DISMISS_ALL_NOTIFICATIONS } from './mutations';
import { NOTIFICATION_MARKED_AS_READ, NOTIFICATION_DISMISSED, ALL_NOTIFICATIONS_DISMISSED, NOTIFICATION_SENT } from './subscriptions';
import { useSimpleError } from 'hooks/apolloWrappers';
import { memoize, uniqBy } from 'lodash';
import { Grow, ClickAwayListener } from '@mui/material';
import NotificationListComponent from './NotificationListComponent';
import { NotificationsLinks, NotificationsSpan, NotificationsPopper, NotificationPaper } from './NotificationComponentsStyled';
import { Typography, IconButton } from '@totalsoft/rocket-ui';

const pageSize = 10;
const defaultLocalState = {
  localOpen: false,
  localNotifications: [],
  localCurrentPage: 1,
  localTotalNotifications: 0,
  localUnreadNotifications: 0
};

function NotificationContainer() {
  const { t } = useTranslation();
  const showError = useSimpleError();

  const [localState, setLocalState] = useState(defaultLocalState);
  const [anchorEl, setAnchorEl] = useState(null);

  const { oidcUser } = useOidcUser(getOidcConfigName());
  const userId = oidcUser?.profile?.sub;

  const { loading, refetch } = useQuery(GET_MY_NOTIFICATIONS, {
    variables: {
      page: localState.localCurrentPage,
      pageSize: pageSize
    },
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const { notifications, unread } = data.getMyNotifications;
      const lastNotifications =
        (notifications &&
          notifications.values.map(item => {
            return {
              ...item,
              translatable: item.deliveryMethod === 'None' ? true : false
            };
          })) ||
        [];

      setLocalState(prevState => ({
        ...prevState,
        localNotifications: uniqBy([...prevState.localNotifications, ...lastNotifications], 'notificationId'),
        localTotalNotifications: notifications && notifications.totalCount,
        localUnreadNotifications: unread
      }));
    }
  });

  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    onError: error => {
      showError(error);
    }
  });

  const [dismissNotification] = useMutation(DISMISS_NOTIFICATION, {
    onError: error => {
      showError(error);
    }
  });

  const [dismissAllNotifications] = useMutation(DISMISS_ALL_NOTIFICATIONS, {
    onError: error => {
      showError(error);
    }
  });

  useSubscription(NOTIFICATION_MARKED_AS_READ, {
    onError: error => showError(error),
    onData: data => {
      const notificationId = data.data?.data?.notificationMarkedAsRead?.notificationId;
      setLocalState(prevState => ({
        ...prevState,
        localUnreadNotifications: prevState.localUnreadNotifications - 1,
        localNotifications: localState.localNotifications.map(x => {
          if (x.notificationId === notificationId) return { ...x, isRead: true };
          return x;
        })
      }));
    }
  });

  useSubscription(NOTIFICATION_DISMISSED, {
    onError: error => showError(error),
    onData: data => {
      const notificationId = data.data?.data?.notificationDismissed?.notificationId;
      setLocalState(prevState => ({
        ...prevState,
        localUnreadNotifications:
          prevState.localNotifications.find(x => x.notificationId === notificationId)?.isRead === false
            ? prevState.localUnreadNotifications - 1
            : prevState.localUnreadNotifications,
        localTotalNotifications: prevState.localTotalNotifications - 1,
        localNotifications: localState.localNotifications.filter(x => x.notificationId !== notificationId)
      }));
    }
  });

  useSubscription(ALL_NOTIFICATIONS_DISMISSED, {
    onError: error => showError(error),
    onData: _data => {
      setLocalState(defaultLocalState);
      refetch({
        page: 1,
        pageSize: pageSize
      });
    }
  });

  useSubscription(NOTIFICATION_SENT, {
    onError: error => showError(error),
    onData: data => {
      if (data.data?.data?.notificationSent?.notificationId)
        setLocalState(prevState => ({
          ...prevState,
          localUnreadNotifications: prevState.localUnreadNotifications + 1,
          localTotalNotifications: prevState.localTotalNotifications + 1,
          localNotifications: [{ ...data.data.data.notificationSent }, ...prevState.localNotifications]
        }));
    }
  });

  const loadMoreNotifications = async () => {
    const lastPage = Math.ceil(localState.localTotalNotifications / pageSize);
    if (localState.localCurrentPage < lastPage && !loading)
      setLocalState({ ...localState, localCurrentPage: localState.localCurrentPage + 1 });
  };

  const handleClick = () => {
    setLocalState({ ...localState, localOpen: !localState.localOpen });
  };

  const handleClose = () => {
    setLocalState({ ...localState, localOpen: false });
  };

  const handleMarkAsRead = memoize(notification => () => {
    setLocalState({ ...localState, localOpen: false });

    if (!notification.isRead)
      markNotificationAsRead({
        variables: {
          notificationId: notification.notificationId,
          userId: userId
        }
      });
  });

  const handleDismiss = memoize(notification => () => {
    dismissNotification({
      variables: {
        notificationId: notification.notificationId,
        userId: userId
      }
    });
  });

  const handleDismissAll = () => {
    handleClose();
    dismissAllNotifications({
      variables: {
        userId: userId
      }
    });
  };

  return (
    <Fragment>
      <IconButton
        color='transparent'
        tooltip={t('Tooltips.Notifications')}
        aria-label='Notifications'
        aria-owns={localState.localOpen ? 'menu-list' : null}
        aria-haspopup='true'
        ref={node => {
          setAnchorEl(node);
        }}
        onClick={handleClick}
      >
        <NotificationsLinks />
        {localState.localUnreadNotifications > 0 && <NotificationsSpan>{localState.localUnreadNotifications}</NotificationsSpan>}
      </IconButton>
      <NotificationsPopper
        placement='bottom-start'
        anchorEl={anchorEl}
        open={localState.localOpen}
        modifiers={[
          {
            name: 'flip'
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'scrollParent'
            }
          }
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Grow in={localState.localOpen} id='menu-list' style={{ transformOrigin: '0 0 0' }}>
            <NotificationPaper>
              {localState.localNotifications.length === 0 ? (
                <Typography sx={{ p: 1 }}>{t('Notifier.NoNotifications')}</Typography>
              ) : (
                <NotificationListComponent
                  notifications={localState.localNotifications}
                  handleDismiss={handleDismiss}
                  handleDismissAll={handleDismissAll}
                  handleMarkAsRead={handleMarkAsRead}
                  loadMoreNotifications={loadMoreNotifications}
                  loading={loading}
                />
              )}
            </NotificationPaper>
          </Grow>
        </ClickAwayListener>
      </NotificationsPopper>
    </Fragment>
  );
}

export default NotificationContainer;
